import type { SetStateAction } from 'react';
import type { StateCreator } from 'zustand';

import type { AgentChatUiMessage } from '~/features/studio/AgentChatThread';
import type { AgentReferenceImage } from '~/features/studio/AgentReferenceImageList';

import {
  type AgentComposerSessionInit,
  type AgentComposerSessionSnapshot,
  emptyAgentComposerSessionSnapshot,
} from '../agentComposerSessionTypes';
import type { WorkspaceStore } from '../workspaceStore';

const resolveSetStateAction = <T>(current: T, action: SetStateAction<T>): T => {
  if (typeof action === 'function') {
    return (action as (value: T) => T)(current);
  }
  return action;
};

export type AgentComposerSessionSlice = {
  agentComposerByKey: Record<string, AgentComposerSessionSnapshot>;
  ensureAgentComposerSession: (
    key: string,
    init?: AgentComposerSessionInit
  ) => void;
  patchAgentComposerSession: (
    key: string,
    patch: Partial<AgentComposerSessionSnapshot>
  ) => void;
  resetAgentComposerSession: (
    key: string,
    init?: AgentComposerSessionInit
  ) => void;
  setAgentComposerMessages: (
    key: string,
    action: SetStateAction<AgentChatUiMessage[]>
  ) => void;
  setAgentComposerReferenceImages: (
    key: string,
    action: SetStateAction<AgentReferenceImage[]>
  ) => void;
  resetAgentComposerSessions: (workspaceId?: string) => void;
  migrateAgentComposerSession: (fromKey: string, toKey: string) => void;
};

const patchSession = (
  current: Record<string, AgentComposerSessionSnapshot>,
  key: string,
  patch: Partial<AgentComposerSessionSnapshot>
): Record<string, AgentComposerSessionSnapshot> => ({
  ...current,
  [key]: {
    ...(current[key] ?? emptyAgentComposerSessionSnapshot()),
    ...patch,
  },
});

export const createAgentComposerSessionSlice: StateCreator<
  WorkspaceStore,
  [],
  [],
  AgentComposerSessionSlice
> = (set, get) => ({
  agentComposerByKey: {},
  ensureAgentComposerSession: (key, init) => {
    if (get().agentComposerByKey[key]) {
      return;
    }
    set(state => ({
      agentComposerByKey: {
        ...state.agentComposerByKey,
        [key]: {
          ...emptyAgentComposerSessionSnapshot(init),
          ...(init?.patch ?? {}),
        },
      },
    }));
  },
  patchAgentComposerSession: (key, patch) =>
    set(state => ({
      agentComposerByKey: patchSession(state.agentComposerByKey, key, patch),
    })),
  resetAgentComposerSession: (key, init) =>
    set(state => ({
      agentComposerByKey: {
        ...state.agentComposerByKey,
        [key]: emptyAgentComposerSessionSnapshot(init),
      },
    })),
  setAgentComposerMessages: (key, action) =>
    set(state => {
      const current =
        state.agentComposerByKey[key] ?? emptyAgentComposerSessionSnapshot();
      return {
        agentComposerByKey: patchSession(state.agentComposerByKey, key, {
          messages: resolveSetStateAction(current.messages, action),
        }),
      };
    }),
  setAgentComposerReferenceImages: (key, action) =>
    set(state => {
      const current =
        state.agentComposerByKey[key] ?? emptyAgentComposerSessionSnapshot();
      return {
        agentComposerByKey: patchSession(state.agentComposerByKey, key, {
          referenceImages: resolveSetStateAction(
            current.referenceImages,
            action
          ),
        }),
      };
    }),
  resetAgentComposerSessions: workspaceId => {
    if (!workspaceId) {
      set({ agentComposerByKey: {} });
      return;
    }
    const prefix = `${workspaceId}:`;
    set(state => {
      const next: Record<string, AgentComposerSessionSnapshot> = {};
      for (const [key, value] of Object.entries(state.agentComposerByKey)) {
        if (!key.startsWith(prefix)) {
          next[key] = value;
        }
      }
      return { agentComposerByKey: next };
    });
  },
  migrateAgentComposerSession: (fromKey, toKey) => {
    if (fromKey === toKey) return;
    set(state => {
      const from = state.agentComposerByKey[fromKey];
      if (!from || state.agentComposerByKey[toKey]) {
        return state;
      }
      return {
        agentComposerByKey: {
          ...state.agentComposerByKey,
          [toKey]: { ...from },
        },
      };
    });
  },
});
